import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type Setter = (actions: ReactNode | null) => void;

const ActionsStateContext = createContext<ReactNode | null>(null);
const ActionsSetterContext = createContext<Setter | undefined>(undefined);

// Page-title override — lets views like SuggestionDetail push a
// dynamic title (e.g. "IPL-2026-048 · Submitted") into the top bar
// instead of the static route-based title computed in AppLayout.
const TitleStateContext = createContext<ReactNode | null>(null);
const TitleSetterContext = createContext<Setter | undefined>(undefined);

export function TopBarActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<ReactNode | null>(null);
  const [title, setTitle] = useState<ReactNode | null>(null);
  const actionsSetterRef = useRef<Setter>(setActions);
  const titleSetterRef = useRef<Setter>(setTitle);
  return (
    <ActionsSetterContext.Provider value={actionsSetterRef.current}>
      <ActionsStateContext.Provider value={actions}>
        <TitleSetterContext.Provider value={titleSetterRef.current}>
          <TitleStateContext.Provider value={title}>
            {children}
          </TitleStateContext.Provider>
        </TitleSetterContext.Provider>
      </ActionsStateContext.Provider>
    </ActionsSetterContext.Provider>
  );
}

export function useTopBarActions() {
  const actions = useContext(ActionsStateContext);
  const setActions = useContext(ActionsSetterContext);
  if (!setActions) {
    throw new Error("useTopBarActions must be used within TopBarActionsProvider");
  }
  return { actions, setActions };
}

export function useTopBarTitle() {
  return useContext(TitleStateContext);
}

export function useRegisterTopBarActions(actions: ReactNode | null) {
  const setActions = useContext(ActionsSetterContext);
  if (!setActions) {
    throw new Error(
      "useRegisterTopBarActions must be used within TopBarActionsProvider",
    );
  }
  useEffect(() => {
    setActions(actions);
    return () => setActions(null);
  }, [actions, setActions]);
}

export function useRegisterPageTitle(title: ReactNode | null) {
  const setTitle = useContext(TitleSetterContext);
  if (!setTitle) {
    throw new Error(
      "useRegisterPageTitle must be used within TopBarActionsProvider",
    );
  }
  useEffect(() => {
    setTitle(title);
    return () => setTitle(null);
  }, [title, setTitle]);
}
